/**
 * DashboardSEOAIO — /website-builder/seo (also linked from /settings/seo)
 *
 * Per-page SEO editor. Left rail lists all pages with their SEO completion
 * status. Right pane edits the selected page's title, meta description,
 * Open Graph image + tags, twitter card, canonical URL, robots noindex.
 *
 * Live search-snippet preview shows what Google would render.
 *
 * Backend:
 *   GET /api/builder/pages
 *   GET /api/builder/pages/{page_id}/seo
 *   PUT /api/builder/pages/{page_id}/seo
 *   GET /api/sites/{site_id}/sitemap.xml  (public)
 *   GET /api/sites/{site_id}/robots.txt   (public)
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Search, FileText, Image as ImageIcon, Globe, EyeOff, Eye, Save, Loader2,
  CheckCircle2, AlertTriangle, Link2, ExternalLink, Sparkles, Copy, Check,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const LIMITS = { title: 60, description: 160 };

function Counter({ value, max }) {
  const v = (value || '').length;
  const cls = v > max ? 'text-red-300' : v > max * 0.85 ? 'text-amber-300' : 'text-white/45';
  return <span className={`text-[10px] font-mono ${cls}`}>{v}/{max}</span>;
}

function CompletionPill({ summary }) {
  const score = (summary.has_title ? 1 : 0) + (summary.has_description ? 1 : 0) + (summary.has_og_image ? 1 : 0);
  if (summary.noindex) return <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-400/25">No-index</span>;
  if (score === 3) return <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-400/25">Complete</span>;
  if (score >= 1)  return <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/25">{score}/3</span>;
  return <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white/8 text-white/55">Empty</span>;
}

function GooglePreview({ url, title, description }) {
  return (
    <div className="bg-white rounded-xl px-5 py-4 max-w-xl">
      <p className="text-[12.5px] text-[#4d5156] truncate font-sans">{url || 'https://your-site.com'}</p>
      <p className="text-[18px] text-[#1a0dab] font-medium mt-0.5 leading-snug font-sans truncate" data-testid="seo-preview-title">{title || 'Page title will appear here'}</p>
      <p className="text-[13px] text-[#4d5156] mt-0.5 leading-snug font-sans line-clamp-2" data-testid="seo-preview-description">{description || 'Meta description will appear here. Make it 150–160 characters for the best click-through rate.'}</p>
    </div>
  );
}

function PageRow({ page, active, onClick }) {
  return (
    <button
      onClick={() => onClick(page)}
      className={`w-full text-left px-3.5 py-2.5 rounded-lg transition-all flex items-center gap-2.5 ${
        active
          ? 'bg-violet-500/15 border border-violet-400/30'
          : 'border border-transparent hover:bg-white/[0.04]'
      }`}
      data-testid={`seo-page-row-${page.page_id}`}
    >
      <FileText size={13} className={active ? 'text-violet-300' : 'text-white/55'} />
      <div className="flex-1 min-w-0">
        <p className={`text-[12.5px] font-semibold truncate ${active ? 'text-white' : 'text-white/80'}`}>{page.title || page.page_id}</p>
        <p className="text-[10.5px] text-white/45 truncate font-mono">/{page.page_id === 'home' ? '' : page.page_id}</p>
      </div>
      <CompletionPill summary={page.seo_summary} />
    </button>
  );
}

export default function DashboardSEOAIO() {
  const [pages, setPages]       = useState([]);
  const [siteUrl, setSiteUrl]   = useState('');
  const [activePage, setActive] = useState(null);
  const [seo, setSeo]           = useState(null);
  const [pageUrl, setPageUrl]   = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [siteId,  setSiteId]    = useState('');
  const [copied,  setCopied]    = useState(null);

  const loadPages = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/pages`, { headers: headers() });
      setPages(r.data?.items || []);
      setSiteUrl(r.data?.site_url || '');
      if (r.data?.items?.length && !activePage) setActive(r.data.items[0]);
    } catch { toast.error('Failed to load pages'); }
    finally { setLoading(false); }
  };

  const loadSeo = async (page) => {
    try {
      const r = await axios.get(`${API}/builder/pages/${page.page_id}/seo`, { headers: headers() });
      setSeo(r.data.seo);
      setPageUrl(r.data.url);
    } catch { toast.error('Failed to load SEO'); }
  };

  // Get site_id once for the sitemap URL
  useEffect(() => {
    axios.get(`${API}/builder/content`, { headers: headers() })
      .then((r) => setSiteId(r.data?.site_id || ''))
      .catch(() => {});
  }, []);

  useEffect(() => { loadPages(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (activePage) loadSeo(activePage); }, [activePage]);

  const save = async () => {
    if (!activePage || !seo) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/builder/pages/${activePage.page_id}/seo`, seo, { headers: headers() });
      setSeo(r.data.seo);
      toast.success('SEO saved');
      // Refresh row in left rail
      await loadPages();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const update = (key, val) => setSeo((s) => ({ ...s, [key]: val }));

  const sitemapUrl = siteId ? `${API}/sites/${siteId}/sitemap.xml` : '';
  const robotsUrl  = siteId ? `${API}/sites/${siteId}/robots.txt`  : '';

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1200);
  };

  const titleCount = useMemo(() => (seo?.title || '').length, [seo]);
  const descCount  = useMemo(() => (seo?.description || '').length, [seo]);

  return (
    <div className="p-10 max-w-7xl" data-testid="seo-page">
      <div className="flex items-start justify-between gap-4 mb-7 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2">Marketing · SEO</p>
          <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Search & social</h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">Tune how each page looks in Google, ChatGPT, and Facebook/Twitter shares.</p>
        </div>
        {siteUrl && (
          <a href={siteUrl} target="_blank" rel="noreferrer" className="text-[11px] uppercase tracking-wider font-bold text-violet-300 hover:text-white flex items-center gap-1.5">
            <ExternalLink size={11} /> View live site
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Left rail — page picker */}
        <div className="space-y-3">
          <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold px-2 pt-1 pb-2">Pages</p>
            <div className="space-y-1">
              {loading ? (
                <div className="px-2 py-3 text-[12px] text-white/55 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
              ) : pages.map((p) => (
                <PageRow key={p.page_id} page={p} active={activePage?.page_id === p.page_id} onClick={setActive} />
              ))}
            </div>
          </div>

          {/* Sitemap + robots cards */}
          <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2 flex items-center gap-1.5"><Sparkles size={11} /> Auto-generated</p>
            <div className="space-y-2.5">
              {[
                { label: 'sitemap.xml', url: sitemapUrl, key: 'sitemap' },
                { label: 'robots.txt',  url: robotsUrl,  key: 'robots'  },
              ].map((x) => (
                <div key={x.key} className="bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11.5px] font-mono text-white">{x.label}</span>
                    <button onClick={() => copy(x.url, x.key)} disabled={!x.url} className="p-1 rounded text-white/55 hover:text-white hover:bg-white/5 disabled:opacity-30" data-testid={`seo-copy-${x.key}`}>
                      {copied === x.key ? <Check size={11} /> : <Copy size={11} />}
                    </button>
                  </div>
                  {x.url && <a href={x.url} target="_blank" rel="noreferrer" className="text-[10px] text-violet-300 hover:text-white font-mono mt-0.5 block truncate">{x.url.replace(API, '')}</a>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — editor */}
        <div className="space-y-5">
          {!activePage ? (
            <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-12 text-center">
              <FileText size={28} className="text-white/30 mx-auto mb-3" />
              <p className="text-white/55">Pick a page on the left to edit its SEO.</p>
            </div>
          ) : !seo ? (
            <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-12 flex items-center justify-center gap-2 text-white/55">
              <Loader2 size={14} className="animate-spin" /> Loading SEO…
            </div>
          ) : (
            <>
              {/* Header card */}
              <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5 flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Editing</p>
                  <h2 className="text-xl font-bold text-white font-syne">{activePage.title || activePage.page_id}</h2>
                  <p className="text-[11.5px] text-white/55 truncate font-mono mt-0.5">{pageUrl}</p>
                </div>
                <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all disabled:opacity-50 flex items-center gap-2" data-testid="seo-save">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                </button>
              </div>

              {/* Live Google preview */}
              <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold mb-3 flex items-center gap-1.5"><Search size={11} /> Search result preview</p>
                <GooglePreview url={pageUrl} title={seo.title} description={seo.description} />
              </div>

              {/* Title & description */}
              <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5 space-y-4" data-testid="seo-meta-card">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-[#A5A8C9] text-[11px]">Page title</Label>
                    <Counter value={seo.title} max={LIMITS.title} />
                  </div>
                  <Input value={seo.title || ''} onChange={(e) => update('title', e.target.value)} placeholder="Authentic Indigenous Goods · Direct From Makers" className="bg-white/5 border-white/10 text-white text-sm rounded-xl" data-testid="seo-input-title" />
                  <p className="text-[10.5px] text-white/45 mt-1.5">Shown as the blue link in Google results. Keep it ≤ {LIMITS.title} characters.</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-[#A5A8C9] text-[11px]">Meta description</Label>
                    <Counter value={seo.description} max={LIMITS.description} />
                  </div>
                  <Textarea value={seo.description || ''} onChange={(e) => update('description', e.target.value)} rows={2} placeholder="Shop handmade goods directly from Indigenous makers across North America. Zero buyer fees, every dollar to the artist." className="bg-white/5 border-white/10 text-white text-sm rounded-xl resize-none" data-testid="seo-input-description" />
                  <p className="text-[10.5px] text-white/45 mt-1.5">Shown as the grey text under the link. Sweet spot: 150–160 characters.</p>
                </div>
              </div>

              {/* Open Graph / Twitter */}
              <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5 space-y-4" data-testid="seo-og-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold flex items-center gap-1.5"><ImageIcon size={11} /> Social sharing (Open Graph / Twitter)</p>
                <div>
                  <Label className="text-[#A5A8C9] text-[11px]">Image URL <span className="text-white/40">(1200×630 ideal)</span></Label>
                  <Input value={seo.og_image || ''} onChange={(e) => update('og_image', e.target.value)} placeholder="https://cdn.your-site.com/og.jpg" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="seo-input-og-image" />
                  {seo.og_image && (
                    <img src={seo.og_image} alt="OG preview" className="mt-3 rounded-lg max-h-40 border border-white/10" onError={(e) => { e.target.style.display = 'none'; }} data-testid="seo-og-preview-img" />
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#A5A8C9] text-[11px]">OG title <span className="text-white/40">(falls back to page title)</span></Label>
                    <Input value={seo.og_title || ''} onChange={(e) => update('og_title', e.target.value)} placeholder="Optional override" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="seo-input-og-title" />
                  </div>
                  <div>
                    <Label className="text-[#A5A8C9] text-[11px]">Twitter card type</Label>
                    <select value={seo.twitter_card || 'summary_large_image'} onChange={(e) => update('twitter_card', e.target.value)} className="w-full mt-1.5 bg-white/5 border border-white/10 text-white text-sm rounded-xl px-3 py-2" data-testid="seo-input-twitter-card">
                      <option value="summary_large_image" className="bg-[#15152E]">Large image (recommended)</option>
                      <option value="summary"             className="bg-[#15152E]">Summary</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-[#A5A8C9] text-[11px]">OG description <span className="text-white/40">(falls back to meta description)</span></Label>
                  <Textarea value={seo.og_description || ''} onChange={(e) => update('og_description', e.target.value)} rows={2} placeholder="Optional override for Facebook/LinkedIn previews" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl resize-none" data-testid="seo-input-og-description" />
                </div>
              </div>

              {/* Advanced */}
              <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5 space-y-4" data-testid="seo-advanced-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold flex items-center gap-1.5"><Link2 size={11} /> Advanced</p>
                <div>
                  <Label className="text-[#A5A8C9] text-[11px]">Canonical URL <span className="text-white/40">(optional)</span></Label>
                  <Input value={seo.canonical_url || ''} onChange={(e) => update('canonical_url', e.target.value)} placeholder={pageUrl} className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl font-mono" data-testid="seo-input-canonical" />
                  <p className="text-[10.5px] text-white/45 mt-1.5">Use only if this page also lives at another URL. Tells Google which version to rank.</p>
                </div>
                <div className="flex items-start justify-between gap-3 p-3 rounded-xl border border-white/[0.04] bg-white/[0.02]">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {seo.noindex ? <EyeOff size={13} className="text-red-300" /> : <Eye size={13} className="text-emerald-300" />}
                      <span className="text-white text-[13px] font-semibold">Hide from search engines</span>
                    </div>
                    <p className="text-[11px] text-white/55 mt-1">When on, Google and other engines won&apos;t index this page. Also excludes it from your sitemap.xml.</p>
                  </div>
                  <button
                    onClick={() => update('noindex', !seo.noindex)}
                    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${seo.noindex ? 'bg-red-500' : 'bg-white/15'}`}
                    aria-pressed={seo.noindex}
                    data-testid="seo-toggle-noindex"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${seo.noindex ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Helpful checklist */}
              <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold mb-3">Health check</p>
                <ul className="space-y-1.5">
                  {[
                    { ok: titleCount > 0 && titleCount <= LIMITS.title, label: `Title is ${titleCount === 0 ? 'missing' : titleCount > LIMITS.title ? 'too long' : 'a good length'}` },
                    { ok: descCount  > 0 && descCount  <= LIMITS.description, label: `Description is ${descCount  === 0 ? 'missing' : descCount  > LIMITS.description ? 'too long' : 'a good length'}` },
                    { ok: !!seo.og_image, label: seo.og_image ? 'Open Graph image is set' : 'Open Graph image is missing (link previews show your logo only)' },
                    { ok: !seo.noindex, label: seo.noindex ? 'This page is hidden from search engines' : 'Page is indexable' },
                  ].map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12.5px]">
                      {c.ok
                        ? <CheckCircle2 size={13} className="text-emerald-400 mt-0.5 shrink-0" />
                        : <AlertTriangle size={13} className="text-amber-400 mt-0.5 shrink-0" />}
                      <span className={c.ok ? 'text-white/75' : 'text-amber-200'}>{c.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
